import type { EntityDB } from "./db";
import type { EmbeddingDB } from "./db/embedding-db";
import {
  getVisibleContentVisibilities,
  type BaseEntity,
  type ContentVisibility,
} from "./types";
import { entities } from "./schema/entities";
import { embeddings } from "./schema/embeddings";
import {
  eq,
  and,
  desc,
  asc,
  sql,
  isNotNull,
  inArray,
  type SQL,
} from "drizzle-orm";
import { z, type Logger } from "@brains/utils";
import type { EntitySerializer } from "./entity-serializer";
import { normalizeEntityRow, type EntityData } from "./entity-data";

/**
 * Schema for sort field
 */
const sortFieldSchema = z.object({
  field: z.string(),
  direction: z.enum(["asc", "desc"]),
  nullsFirst: z.boolean().optional(),
});

/**
 * Schema for list entities options
 */
const listOptionsSchema = z.object({
  limit: z.number().int().positive().optional(),
  offset: z.number().int().min(0).optional().default(0),
  sortFields: z.array(sortFieldSchema).optional(),
  filter: z
    .object({
      metadata: z.record(z.string(), z.unknown()).optional(),
      visibilityScope: z.enum(["public", "shared", "restricted"]).optional(),
    })
    .optional(),
  /** Filter to only entities with metadata.status = "published" */
  publishedOnly: z.boolean().optional(),
});

type ListOptions = z.input<typeof listOptionsSchema>;

/**
 * EntityQueries handles database query operations for entities
 * Extracted from EntityService for single responsibility
 */
export interface EntityQueryDeps {
  db: EntityDB;
  serializer: EntitySerializer;
  logger: Logger;
  /** Embedding DB for delete cascading (separate from entity DB). */
  embeddingDb: EmbeddingDB;
}

export class EntityQueries {
  private db: EntityDB;
  private embeddingDb: EmbeddingDB;
  private serializer: EntitySerializer;
  private logger: Logger;

  constructor(deps: EntityQueryDeps) {
    this.db = deps.db;
    this.embeddingDb = deps.embeddingDb;
    this.serializer = deps.serializer;
    this.logger = deps.logger.child("EntityQueries");
  }

  /**
   * Get an entity by ID from database.
   * Fail closed: undefined visibilityScope filters to public-only.
   */
  public async getEntityData(
    entityType: string,
    id: string,
    visibilityScope?: ContentVisibility,
  ): Promise<EntityData | null> {
    this.logger.debug(`Getting entity of type ${entityType} with ID ${id}`);

    const scope: ContentVisibility = visibilityScope ?? "public";
    const conditions: SQL[] = [
      eq(entities.id, id),
      eq(entities.entityType, entityType),
    ];
    if (scope !== "restricted") {
      conditions.push(
        inArray(entities.visibility, getVisibleContentVisibilities(scope)),
      );
    }

    const result = await this.db
      .select()
      .from(entities)
      .where(and(...conditions))
      .limit(1);

    if (result.length === 0) {
      this.logger.debug(`Entity of type ${entityType} with ID ${id} not found`);
      return null;
    }

    const row = result[0];
    if (!row) {
      return null;
    }

    return normalizeEntityRow(row);
  }

  /**
   * List entities by type with pagination
   */
  public async listEntities<T extends BaseEntity>(
    entityType: string,
    options: ListOptions = {},
  ): Promise<T[]> {
    const validatedOptions = listOptionsSchema.parse(options);
    const { limit, offset, sortFields, filter, publishedOnly } =
      validatedOptions;

    this.logger.debug(
      `Listing entities of type ${entityType} (limit: ${limit}, offset: ${offset}, filter: ${JSON.stringify(filter)}, publishedOnly: ${publishedOnly})`,
    );

    const whereConditions = this.buildWhereConditions(
      entityType,
      publishedOnly,
      filter?.metadata,
      filter?.visibilityScope,
    );
    const orderByClauses = this.buildOrderByClauses(sortFields);

    const query = this.db
      .select()
      .from(entities)
      .where(and(...whereConditions))
      .orderBy(...orderByClauses)
      .offset(offset);

    const result = limit !== undefined ? await query.limit(limit) : await query;

    // Convert from database format to entities
    const entityList = await this.serializer.convertToEntities<T>(
      result.map(normalizeEntityRow),
      entityType,
    );

    this.logger.debug(
      `Listed ${entityList.length} entities of type ${entityType}`,
    );

    return entityList;
  }

  /**
   * Build WHERE conditions for entity queries.
   * Shared by listEntities and countEntities.
   */
  private buildWhereConditions(
    entityType: string,
    publishedOnly?: boolean,
    metadataFilter?: Record<string, unknown>,
    visibilityScope?: ContentVisibility,
  ): SQL[] {
    const conditions: SQL[] = [eq(entities.entityType, entityType)];

    if (publishedOnly) {
      conditions.push(
        sql`(json_extract(${entities.metadata}, '$.status') = 'published' OR json_extract(${entities.metadata}, '$.status') = 'active' OR json_extract(${entities.metadata}, '$.status') IS NULL)`,
      );
    }

    // Fail closed: undefined scope filters to public-only.
    const scope: ContentVisibility = visibilityScope ?? "public";
    if (scope !== "restricted") {
      conditions.push(
        inArray(entities.visibility, getVisibleContentVisibilities(scope)),
      );
    }

    if (metadataFilter) {
      for (const [key, value] of Object.entries(metadataFilter)) {
        if (value !== undefined) {
          const jsonPath = `$.${key}`;
          conditions.push(
            sql`json_extract(${entities.metadata}, ${jsonPath}) = ${value}`,
          );
        }
      }
    }

    return conditions;
  }

  /**
   * Build ORDER BY clauses from sortFields.
   * Supports system fields (created, updated) and metadata fields via json_extract.
   */
  private buildOrderByClauses(sortFields?: ListOptions["sortFields"]): SQL[] {
    // Default: sort by updated desc
    if (!sortFields || sortFields.length === 0) {
      return [desc(entities.updated)];
    }

    return sortFields.flatMap(({ field, direction, nullsFirst }) => {
      const orderFn = direction === "desc" ? desc : asc;

      // System fields
      if (field === "created") {
        const fieldExpr = entities.created;
        if (nullsFirst) {
          return [asc(isNotNull(fieldExpr)), orderFn(fieldExpr)];
        }
        return [orderFn(fieldExpr)];
      }

      if (field === "updated") {
        const fieldExpr = entities.updated;
        if (nullsFirst) {
          return [asc(isNotNull(fieldExpr)), orderFn(fieldExpr)];
        }
        return [orderFn(fieldExpr)];
      }

      // Metadata fields - use json_extract
      const fieldExpression = sql`json_extract(${entities.metadata}, '$.' || ${field})`;

      // Handle nullsFirst: put NULL values before non-NULL values
      if (nullsFirst) {
        return [
          asc(isNotNull(fieldExpression)), // Always ASC: false (NULL) before true (NOT NULL)
          orderFn(fieldExpression),
        ];
      }

      return [orderFn(fieldExpression)];
    });
  }

  /**
   * Count entities of a specific type with optional filters
   * Used for database-level pagination
   */
  public async countEntities(
    entityType: string,
    options: {
      publishedOnly?: boolean;
      filter?: {
        metadata?: Record<string, unknown>;
        visibilityScope?: ContentVisibility;
      };
    } = {},
  ): Promise<number> {
    const whereConditions = this.buildWhereConditions(
      entityType,
      options.publishedOnly,
      options.filter?.metadata,
      options.filter?.visibilityScope,
    );

    const result = await this.db
      .select({ count: sql<number>`COUNT(*)` })
      .from(entities)
      .where(and(...whereConditions));

    return Number(result[0]?.count ?? 0);
  }

  /**
   * Get entity counts grouped by type.
   * Fail closed: undefined visibilityScope filters to public-only.
   */
  public async getEntityCounts(
    visibilityScope?: ContentVisibility,
  ): Promise<Array<{ entityType: string; count: number }>> {
    this.logger.debug("Getting entity counts by type");

    const scope: ContentVisibility = visibilityScope ?? "public";
    const conditions: SQL[] = [];
    if (scope !== "restricted") {
      conditions.push(
        inArray(entities.visibility, getVisibleContentVisibilities(scope)),
      );
    }

    const baseQuery = this.db
      .select({
        entityType: entities.entityType,
        count: sql<number>`COUNT(*)`.as("count"),
      })
      .from(entities);

    const result = await (conditions.length > 0
      ? baseQuery.where(and(...conditions)).groupBy(entities.entityType)
      : baseQuery.groupBy(entities.entityType));

    this.logger.debug(`Found ${result.length} entity types`);

    return result.map((row) => ({
      entityType: row.entityType,
      count: Number(row.count),
    }));
  }

  /**
   * Check if entity exists
   */
  public async entityExists(entityType: string, id: string): Promise<boolean> {
    const result = await this.db
      .select({ id: entities.id })
      .from(entities)
      .where(and(eq(entities.entityType, entityType), eq(entities.id, id)))
      .limit(1);

    return result.length > 0;
  }

  /**
   * Delete an entity by type and ID
   */
  public async deleteEntity(entityType: string, id: string): Promise<boolean> {
    this.logger.debug(`Deleting entity of type ${entityType} with ID ${id}`);

    // First check if entity exists
    const exists = await this.entityExists(entityType, id);

    if (!exists) {
      this.logger.debug(
        `Entity of type ${entityType} with ID ${id} not found for deletion`,
      );
      return false;
    }

    // Delete embedding first (no foreign key constraint, so manual cascade)
    // Uses embedding DB (which may be separate from entity DB)
    await this.embeddingDb
      .delete(embeddings)
      .where(
        and(eq(embeddings.entityType, entityType), eq(embeddings.entityId, id)),
      );

    // Delete FTS5 index entry
    await this.db.run(
      sql`DELETE FROM entity_fts WHERE entity_id = ${id} AND entity_type = ${entityType}`,
    );

    // Delete entity
    await this.db
      .delete(entities)
      .where(and(eq(entities.entityType, entityType), eq(entities.id, id)));

    this.logger.debug(`Deleted entity of type ${entityType} with ID ${id}`);
    return true;
  }
}
